import type { Book, Task } from "#/types";

import { cancelJob as apiCancelJob, getJobs, getLibrary } from "#/client";
import { useEffect, useState } from "react";
import { useSignal } from "#/use-signal";
import { bookIdFilterSignal, sourceFilterSignal } from "./job-filters";

const DEFAULT_LIMIT = 50;

export function useJobs() {
  // Read filter values from signals
  const bookIdFilters = useSignal(bookIdFilterSignal);
  const sourceFilter = useSignal(sourceFilterSignal);

  const [jobs, setJobs] = useState<Task[]>([]);
  const [library, setLibrary] = useState<Book[]>([]);
  const [loadingState, setLoadingState] = useState(true);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async (cursor?: number) => {
    try {
      if (cursor === undefined) {
        setIsRefreshing(true);
      }

      // If we have multiple book_ids, we need to fetch jobs for each and merge them
      let allJobs: Task[] = [];
      let finalNextCursor: number | null = null;

      if (bookIdFilters.length > 0) {
        // Fetch jobs for each book_id
        const jobPromises = bookIdFilters.map((bookId: number) =>
          getJobs({
            limit: DEFAULT_LIMIT,
            book_id: bookId,
            source: sourceFilter || undefined,
          }),
        );

        const jobsDataArray = await Promise.all(jobPromises);
        allJobs = jobsDataArray.flatMap(
          (data: { items: Task[]; next_cursor: number | null }) => data.items,
        );

        // For simplicity, we don't support pagination with multiple book filters
        // Just take the highest next cursor if any exist
        const cursors = jobsDataArray
          .map((data: { next_cursor: number | null }) => data.next_cursor)
          .filter((c: number | null): c is number => c !== null);
        finalNextCursor = cursors.length > 0 ? Math.max(...cursors) : null;
      } else {
        // Single request with source filter or no filter
        const options: {
          cursor?: number;
          limit: number;
          source?: string;
        } = {
          limit: DEFAULT_LIMIT,
        };

        if (cursor !== undefined) {
          options.cursor = cursor;
        }

        if (sourceFilter) {
          options.source = sourceFilter;
        }

        const jobsData = await getJobs(options);
        allJobs = jobsData.items;
        finalNextCursor = jobsData.next_cursor;
      }

      // Sort jobs by created_at descending
      allJobs.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      // Fetch library for book titles
      const libData = await getLibrary();

      if (cursor !== undefined) {
        // Loading more - append to existing jobs
        setJobs((prev) => [...prev, ...allJobs]);
        setNextCursor(finalNextCursor);
        setHasMore(finalNextCursor !== null);
        setLoadingMore(false);
      } else {
        // Initial load - replace jobs
        setJobs(allJobs);
        setNextCursor(finalNextCursor);
        setHasMore(finalNextCursor !== null);
        setLoadingState(false);
      }

      setLibrary(libData);
    } catch (error) {
      console.error("Failed to fetch jobs data:", error);
      if (cursor !== undefined) {
        setLoadingMore(false);
      } else {
        setLoadingState(false);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadMore = () => {
    if (nextCursor !== null && !loadingMore && hasMore && bookIdFilters.length === 0) {
      // Only support load more for non-filtered views
      setLoadingMore(true);
      void fetchData(nextCursor);
    }
  };

  const cancelJob = async (jobId: number) => {
    try {
      await apiCancelJob(jobId);
      void fetchData();
    } catch (error) {
      console.error("Failed to cancel job:", error);
      alert(`Failed to cancel job: ${String(error)}`);
    }
  };

  useEffect(() => {
    setJobs([]);
    setNextCursor(null);
    setHasMore(true);
    setLoadingState(true);
    void fetchData();
    // Re-fetch when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookIdFilters, sourceFilter]);

  return {
    jobs,
    library,
    loadingState,
    loadingMore,
    hasMore,
    cancelJob,
    loadMore,
    fetchData,
    isRefreshing,
  };
}
